import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { matchPath } from 'react-router';
import allRouteList from 'routes/PageRouteList/allRouteList';
import { accessType } from '../../constants';

// This Higher Order Component (HOC) redirects them to the appropriate login page
// if they are not authenticated.
const withRedirectToLogin = (Component) => {
   /**
    * Retrieves the current login pathname for the current route. The current route
    * must require authorization to be redirected to the appropriate login page.
    */
   const getLogInPathname = (pathName) => {
      let loginPathname = null;

      const currentRoute = allRouteList.find((routeItem) => {
         let matchedPath = false;
         if (routeItem?.path && pathName) {
            matchedPath = matchPath(pathName, {
               path: routeItem.path,
               exact: routeItem?.exact,
            });
         }

         return matchedPath;
      });

      // Current route requires employer authentication.
      if (currentRoute?.access?.type === accessType.privilege
         || (
            currentRoute?.access?.type === accessType.role
            && currentRoute?.access?.value?.employer?.length > 0
         )
      ) {
         loginPathname = '/auth/login/employer';

         // Current route requires partner authentication.
      } else if (currentRoute?.access?.type === accessType.role
         && currentRoute?.access?.value?.partner?.length > 0) {
         loginPathname = '/auth/login/jobcase';
      }

      return loginPathname;
   };

   const mapStateToProps = (state) => {
      return {
         isLoggedInPartner: !!(state.authReducer?.isLoggedInPartner),
         isLoggedInEmployer: !!(state.authReducer?.isLoggedIn),
         isLoading: !!(state.authReducer?.global?.isLoading),
      };
   };

   const InnerWithRedirectToLoginHOC = ({ isLoading, isLoggedInEmployer, isLoggedInPartner, ...restProps }) => {
      const location = useLocation();
      const history = useHistory();

      // Used for handling the state delay for isLoading
      const [isReadyToMountComponent, setIsReadyToMountComponent] = useState(false);

      useEffect(() => {
         if (!isLoading && !isLoggedInEmployer && !isLoggedInPartner) {
            const loginPathname = getLogInPathname(location.pathname);
            if (loginPathname) {
               history.push({
                  pathname: loginPathname,
                  state: { from: `${ location.pathname }${ location.search }` },
               });
            }
         } else if (!isLoading) {
            setIsReadyToMountComponent(true);
         }
      }, [isLoggedInEmployer, isLoading]);

      return (
         <>
            {
               isReadyToMountComponent
               && <Component {...restProps}/>
            }
         </>
      );
   };

   InnerWithRedirectToLoginHOC.propTypes = {
      isLoggedInEmployer: PropTypes.bool.isRequired,
      isLoggedInPartner: PropTypes.bool.isRequired,
      isLoading: PropTypes.bool.isRequired,
   };

   return connect(mapStateToProps)(InnerWithRedirectToLoginHOC);
};

export default withRedirectToLogin;
